import LandingLayout from '../../components/layout/LandingLayout';

export default function PrivacyPolicy() {
  return (
    <LandingLayout>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <h1 className="text-3xl font-extrabold text-neutral-900 mb-2">Privacy Policy</h1>
        <p className="text-sm text-neutral-500 mb-10">Last updated: April 24, 2026</p>

        <div className="prose prose-neutral max-w-none space-y-8 text-neutral-700 leading-relaxed">

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">1. Who we are</h2>
            <p>
              Sage Mental Inc. (&quot;Sage,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) operates the Sage practice management platform
              available at sagemental.net. We are a HIPAA-covered business associate for the healthcare
              providers who use our platform, and we take the privacy of both providers and their clients
              seriously.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">2. Information we collect</h2>
            <p className="mb-3"><strong>Account information.</strong> When you register, we collect your name, email address, and a hashed password. For practices, we also collect practice name, NPI, and billing contact details.</p>
            <p className="mb-3"><strong>Protected Health Information (PHI).</strong> If you are a provider using Sage to manage your practice, you may enter PHI on behalf of your clients — including appointment records, clinical notes, diagnoses, and billing information. We store and process this PHI solely to provide the platform services and as described in our Business Associate Agreement.</p>
            <p><strong>Usage data.</strong> We collect standard server logs (IP addresses, browser type, pages visited, timestamps) to operate and improve the service. We do not sell this data.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">3. How we use information</h2>
            <ul className="list-disc pl-5 space-y-2">
              <li>To provide, maintain, and improve the Sage platform</li>
              <li>To authenticate users and enforce session security</li>
              <li>To send transactional emails (appointment reminders, password resets, billing receipts)</li>
              <li>To comply with legal obligations, including HIPAA</li>
              <li>To investigate and prevent fraud, abuse, or security incidents</li>
            </ul>
            <p className="mt-3">We do not use PHI for advertising, analytics benchmarking, or any purpose beyond operating the platform.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">4. HIPAA compliance</h2>
            <p>
              Sage is designed to be HIPAA compliant. We enter into a Business Associate Agreement (BAA)
              with every covered entity that uses our platform. PHI is encrypted in transit (TLS 1.2+)
              and at rest (AES-256). Access to PHI is logged in our audit system. We do not disclose PHI
              to any third party without a BAA in place, except as required by law.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">5. Data sharing</h2>
            <p className="mb-3">
              We share information only with subprocessors necessary to deliver the service (cloud
              infrastructure, email delivery, payment processing), all of whom have signed BAAs where
              required. We do not sell personal data or PHI.
            </p>
            <p>We may disclose information if required by a valid legal process, subpoena, or court order.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">6. Data retention</h2>
            <p>
              We retain account data for as long as your account is active plus a reasonable period
              thereafter for legal and audit purposes. PHI retention follows the requirements of your
              BAA and applicable state law. You may request deletion of your account and associated
              data by contacting us.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">7. Your rights</h2>
            <p>
              Depending on your jurisdiction, you may have the right to access, correct, or delete
              personal data we hold about you. To exercise these rights, email us at{' '}
              <a href="mailto:privacy@sagemental.net" className="text-primary-600 hover:underline">privacy@sagemental.net</a>.
              For requests related to PHI, please contact your provider directly.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">8. Security</h2>
            <p>
              We implement administrative, physical, and technical safeguards consistent with HIPAA
              Security Rule requirements, including encryption, access controls, audit logging, and
              regular security assessments.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">9. Changes to this policy</h2>
            <p>
              We may update this policy from time to time. We will notify registered users of material
              changes by email or in-app notice at least 30 days before the change takes effect.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-neutral-900 mb-3">10. Contact us</h2>
            <p>
              Sage Mental Inc.<br />
              Privacy Officer<br />
              <a href="mailto:privacy@sagemental.net" className="text-primary-600 hover:underline">privacy@sagemental.net</a>
            </p>
          </section>

        </div>
      </div>
    </LandingLayout>
  );
}
